import { useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { v7 as uuidv7 } from 'uuid';
import { PdmApi } from '../../api/PdmApi';
import {
  ENotificationType,
  ENotificationSource,
  ENotificationExecution,
  ELovCodes,
} from '../../data/Constants';

import type { AppDispatch, AppState } from '../../store/AppStore';
import type { ILovDetail, INotificationRequest } from '../../../types';

/** Props for the {@link PublishProductData} component. */
interface IPublishProductDataProps {
  /** The ID of the product to publish. */
  productId: string;
  /** Callback invoked when the dialog should be closed. */
  onClose: () => void;
}

/**
 * Form component that allows the user to select consumer targets and publish
 * product master data by dispatching a notification request.
 *
 * @param props - {@link IPublishProductDataProps}
 */
export const PublishProductData = (props: IPublishProductDataProps): React.ReactElement => {
  const { productId, onClose } = props;
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();

  const listOfValues = useSelector((state: AppState) => state.pdm.listOfValues);
  const consumerOptions: ILovDetail[] =
    listOfValues.find((lov) => lov.code === ELovCodes.Econ)?.options ?? [];

  const [selectedConsumers, setSelectedConsumers] = useState<string[]>([]);

  const allSelected =
    consumerOptions.length > 0 && selectedConsumers.length === consumerOptions.length;
  const someSelected = selectedConsumers.length > 0 && !allSelected;

  /** Selects or deselects all consumer checkboxes. */
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedConsumers(consumerOptions.map((opt) => opt.code));
    } else {
      setSelectedConsumers([]);
    }
  };

  /**
   * Toggles the selection state of a single consumer checkbox.
   * @param code - The LOV code of the consumer option to toggle.
   */
  const handleCheckboxChange = (code: string) => {
    setSelectedConsumers((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  /** Builds a notification request from the selected consumers and dispatches it, then closes the dialog. */
  const handlePublishProductData = () => {
    const selectedNames = consumerOptions
      .filter((opt) => selectedConsumers.includes(opt.code))
      .map((opt) => opt.name)
      .join(',');

    const notificationRequest: INotificationRequest = {
      id: uuidv7(),
      type: ENotificationType.PublishMasterData,
      source: ENotificationSource.PDM,
      details: {
        productId: productId,
        application: selectedNames,
        execute: ENotificationExecution.ProductDataSyncInFlight,
      },
    };

    PdmApi.sendNotification(notificationRequest, dispatch);
    onClose();
  };

  /** Cancels the publish action and closes the dialog without dispatching. */
  const handleCancel = () => {
    onClose();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <fieldset className="inputFieldset w-100 mt-1em">
        <legend className="inputLegend">{t('labels.consumers')}</legend>
        <FormControlLabel
          label={t('labels.selectAll')}
          labelPlacement="end"
          control={
            <Checkbox
              color="primary"
              indeterminate={someSelected}
              checked={allSelected}
              onChange={handleSelectAll}
            />
          }
        />
        <div className="checkbox-grid">
          {consumerOptions.map((option) => {
            const isSelected = selectedConsumers.includes(option.code);
            return (
              <FormControlLabel
                key={option.code}
                label={option.name}
                labelPlacement="end"
                control={
                  <Checkbox
                    checked={isSelected}
                    onChange={() => handleCheckboxChange(option.code)}
                  />
                }
              />
            );
          })}
        </div>
      </fieldset>
      <Box className="formActionButtons" sx={{ mt: 2 }}>
        <Button
          variant="contained"
          size="medium"
          className="text-capitalize"
          disabled={selectedConsumers.length === 0}
          onClick={handlePublishProductData}
        >
          {t('button.publish')}
        </Button>
        <Button size="medium" className="text-capitalize" onClick={handleCancel}>
          {t('button.cancel')}
        </Button>
      </Box>
    </Box>
  );
};
